import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { toast } from "sonner";
import {
  ArrowLeft, Zap, AlertCircle, CheckCircle2, RefreshCw,
  Send, Trash2, Save, Sparkles, X, ArrowUpCircle, Grid3x3,
} from "lucide-react";

/**
 * Automation → Meta template mapping console.
 *
 * Route: /app/whatsapp/automations
 * Shows every proactive automation, its stable key, its current mapping
 * (or "Template required"), and lets admins bind an approved Meta template
 * + reorder the params. Reads the live approved-template list from Meta.
 */
export default function AutomationTemplates() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);   // dry-run auto-map proposal
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/whatsapp/automations");
      setData(r.data);
    } catch (e) { toast.error(apiError(e, "Failed to load automations")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const templatesByName = useMemo(() => {
    const m = new Map();
    (data?.approved_templates || []).forEach(t => m.set(t.name, t));
    return m;
  }, [data]);

  const openAutoMap = async () => {
    setPreviewLoading(true);
    setPreviewOpen(true);
    try {
      const r = await api.post("/whatsapp/automations/auto-map-matching?apply=false");
      setPreview(r.data);
    } catch (e) {
      toast.error(apiError(e, "Failed to compute auto-map proposal"));
      setPreviewOpen(false);
    } finally { setPreviewLoading(false); }
  };

  const applyAutoMap = async () => {
    setApplying(true);
    try {
      const r = await api.post("/whatsapp/automations/auto-map-matching?apply=true");
      const n = (r.data.matched || []).length;
      toast.success(n ? `Auto-mapped ${n} automation${n === 1 ? "" : "s"}` : "No new mappings applied");
      setPreviewOpen(false);
      setPreview(null);
      load();
    } catch (e) { toast.error(apiError(e, "Auto-map failed")); }
    finally { setApplying(false); }
  };

  if (loading && !data) return <div className="p-8 text-sm text-ink-mid">Loading…</div>;
  if (!data) return null;

  const total = data.automations.length;
  const mapped = data.automations.filter(a => a.status === "mapped").length;
  const withUpgrades = data.automations.filter(a => (a.upgrade_candidates || []).length > 0).length;

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-6 py-8 space-y-6" data-testid="automation-templates-page">
      <div>
        <Link to="/app/whatsapp" className="inline-flex items-center gap-1.5 text-xs text-ink-mid hover:text-ink"
              data-testid="auto-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to WhatsApp
        </Link>
        <div className="mt-3 flex items-start justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl text-ink">Automation Templates</h1>
            <p className="mt-1 text-sm text-ink-mid max-w-2xl">
              Bind each proactive WhatsApp automation to a Meta-approved template. Outside the 24-hour
              customer-service window, unmapped automations are <strong>blocked</strong> — Viyora will
              never send freeform text without an open session.
            </p>
          </div>
          <button onClick={load} className="inline-flex items-center gap-2 text-xs text-ink-mid hover:text-ink" data-testid="auto-reload">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Reload
          </button>
        </div>
        <div className="mt-3">
          <Link to="/app/whatsapp/automations/matrix"
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-800 hover:bg-indigo-100"
                data-testid="matrix-link">
            <Grid3x3 className="w-3.5 h-3.5" /> View intent-scoped Mapping Matrix (read-only)
          </Link>
        </div>
        <div className="mt-4 flex items-center gap-3 text-[11px] flex-wrap">
          <span className="px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800"
                data-testid="auto-mapped-count">
            {mapped} mapped
          </span>
          <span className="px-3 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-900"
                data-testid="auto-unmapped-count">
            {total - mapped} require template
          </span>
          <span className="px-3 py-1 rounded-full bg-stone-100 border border-stone-200 text-ink-mid">
            {data.approved_templates.length} approved Meta templates
          </span>
          {withUpgrades > 0 && (
            <span className="px-3 py-1 rounded-full bg-violet-50 border border-violet-200 text-violet-800 inline-flex items-center gap-1"
                  data-testid="auto-upgrades-count">
              <ArrowUpCircle className="w-3 h-3" /> {withUpgrades} upgrade{withUpgrades === 1 ? "" : "s"} available
            </span>
          )}
          <button
            onClick={openAutoMap}
            className="ml-auto inline-flex items-center gap-2 text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 px-4 py-1.5 rounded-full transition-colors"
            data-testid="auto-map-matching-btn">
            <Sparkles className="w-3.5 h-3.5" /> Auto-map matching automations
          </button>
        </div>
      </div>

      {!data.clinic_connected && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="auto-not-connected">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <div>Meta Cloud API is not connected for this clinic. <Link to="/app/whatsapp/connect-meta" className="underline">Connect first →</Link></div>
          </div>
        </div>
      )}

      {data.template_fetch_error && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900" data-testid="auto-fetch-error">
          Could not fetch approved templates from Meta: <code className="text-[11px]">{JSON.stringify(data.template_fetch_error)}</code>
        </div>
      )}

      <div className="space-y-3">
        {data.automations.map(a => (
          <AutomationRow key={a.key} a={a} templates={data.approved_templates}
                          templatesByName={templatesByName}
                          availableParameters={data.available_parameters || []}
                          onSaved={load} />
        ))}
      </div>

      {previewOpen && (
        <AutoMapPreviewDialog
          preview={preview}
          loading={previewLoading}
          applying={applying}
          onApply={applyAutoMap}
          onClose={() => { setPreviewOpen(false); setPreview(null); }}
        />
      )}
    </div>
  );
}

/* ─────────────────────────  Auto-map Preview Dialog ───────────────────── */
function AutoMapPreviewDialog({ preview, loading, applying, onApply, onClose }) {
  const matched   = preview?.matched || [];
  const skipped   = preview?.already_mapped_skipped || [];
  const ambiguous = preview?.ambiguous || [];
  const unmatched = preview?.available_unmatched || [];
  const skippedWithUpgrades = skipped.filter(s => (s.upgrade_candidates || []).length > 0);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
         data-testid="auto-map-preview-dialog">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl border border-[#E7E1D3] overflow-hidden">
        <div className="p-5 border-b border-[#E7E1D3] flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-emerald-50 grid place-items-center text-emerald-700">
            <Sparkles className="w-4 h-4" />
          </div>
          <div className="flex-1">
            <h2 className="font-display text-lg text-ink">Auto-map matching automations</h2>
            <p className="text-xs text-ink-mid mt-0.5">
              Only high-confidence matches (exact / versioned / prefix). Existing mappings are never overwritten.
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-linen text-ink-mid" data-testid="auto-map-preview-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {loading ? (
          <div className="p-10 text-center text-sm text-ink-mid">
            <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" /> Analysing templates…
          </div>
        ) : preview ? (
          <div className="p-5 space-y-5 max-h-[60vh] overflow-y-auto" data-testid="auto-map-preview-body">
            {/* Matched — will be applied */}
            <Section
              title={`Will be auto-mapped (${matched.length})`}
              tint="emerald"
              testid="preview-matched"
              empty="No new high-confidence matches found.">
              {matched.map(m => (
                <div key={m.automation} className="flex items-center justify-between gap-3 py-2 border-b last:border-0 border-[#F1EBDD]"
                     data-testid={`preview-matched-${m.automation}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-ink truncate">{m.automation}</div>
                    <div className="text-[11px] text-ink-mid font-mono truncate">→ {m.template}</div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <ConfidencePill c={m.confidence} />
                    {m.category_match === false && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-900">
                        category mismatch
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </Section>

            {/* Already mapped — kept, with upgrade hints */}
            {skippedWithUpgrades.length > 0 && (
              <Section
                title={`Existing mappings kept · ${skippedWithUpgrades.length} with upgrades`}
                tint="violet"
                testid="preview-skipped-upgrades">
                <p className="text-[11px] text-ink-mid mb-2">
                  These automations already have a template. We kept them as-is. You can review the upgrade on the row and apply manually.
                </p>
                {skippedWithUpgrades.map(s => (
                  <div key={s.automation} className="py-2 border-b last:border-0 border-[#F1EBDD]"
                       data-testid={`preview-upgrade-${s.automation}`}>
                    <div className="text-sm font-medium text-ink">{s.automation}</div>
                    <div className="text-[11px] text-ink-mid font-mono">Current: {s.current_template}</div>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {(s.upgrade_candidates || []).map(u => (
                        <span key={u.template}
                              className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-violet-50 border border-violet-200 text-violet-800 inline-flex items-center gap-1">
                          <ArrowUpCircle className="w-3 h-3" /> {u.template} <ConfidencePill c={u.confidence} tiny />
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </Section>
            )}

            {/* Ambiguous — leave for manual */}
            {ambiguous.length > 0 && (
              <Section title={`Ambiguous — needs manual review (${ambiguous.length})`} tint="amber" testid="preview-ambiguous">
                {ambiguous.map(a => (
                  <div key={a.automation} className="py-2 border-b last:border-0 border-[#F1EBDD]"
                       data-testid={`preview-ambiguous-${a.automation}`}>
                    <div className="text-sm font-medium text-ink">{a.automation}</div>
                    <div className="text-[11px] text-ink-mid font-mono">
                      Candidates: {a.candidates.join(", ")}
                    </div>
                  </div>
                ))}
              </Section>
            )}

            {/* Unmatched */}
            {unmatched.length > 0 && (
              <Section title={`Unmapped approved templates (${unmatched.length})`} tint="stone" testid="preview-unmatched">
                <div className="text-[11px] text-ink-mid font-mono flex flex-wrap gap-1">
                  {unmatched.map(t => <span key={t.name} className="px-2 py-0.5 bg-stone-100 rounded-full">{t.name}</span>)}
                </div>
              </Section>
            )}
          </div>
        ) : null}

        <div className="p-4 border-t border-[#E7E1D3] flex items-center justify-end gap-2 bg-linen/40">
          <button onClick={onClose} className="text-sm px-4 py-2 rounded-full text-ink-mid hover:bg-white"
                  data-testid="auto-map-preview-cancel">
            Cancel
          </button>
          <button onClick={onApply}
                  disabled={applying || loading || matched.length === 0}
                  className="inline-flex items-center gap-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 px-5 py-2 rounded-full"
                  data-testid="auto-map-preview-apply">
            {applying ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            Apply {matched.length > 0 ? `${matched.length} mapping${matched.length === 1 ? "" : "s"}` : ""}
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, tint = "stone", children, empty, testid }) {
  const tints = {
    emerald: "border-emerald-100 bg-emerald-50/40",
    violet:  "border-violet-100 bg-violet-50/40",
    amber:   "border-amber-100 bg-amber-50/40",
    stone:   "border-stone-100 bg-stone-50/40",
  };
  const isEmpty = !children || (Array.isArray(children) && children.length === 0);
  return (
    <div className={`rounded-xl border ${tints[tint]} p-3`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-ink-mid font-medium mb-2">{title}</div>
      {isEmpty && empty ? <div className="text-xs text-ink-mid italic">{empty}</div> : children}
    </div>
  );
}

function ConfidencePill({ c, tiny = false }) {
  const colors = {
    exact:        "bg-emerald-100 text-emerald-800 border-emerald-200",
    versioned:    "bg-sky-100 text-sky-800 border-sky-200",
    intent_match: "bg-violet-100 text-violet-800 border-violet-200",
    prefix:       "bg-stone-100 text-ink border-stone-200",
  };
  const cls = colors[c] || colors.prefix;
  return (
    <span className={`inline-block text-[9px] font-mono px-1.5 py-0.5 rounded-full border ${cls} ${tiny ? "" : ""}`}>
      {c}
    </span>
  );
}

/* ────────────────────────────  Automation Row ──────────────────────────── */
function AutomationRow({ a, templates, templatesByName, availableParameters = [], onSaved }) {
  const initial = a.mapping || { template: "", lang: "en", params: a.param_slots || [] };
  const upgrades = a.upgrade_candidates || [];
  // INTENT-SCOPED: the picker MUST use compatible_templates, never the
  // global approved list. Backend has already filtered by canonical intent.
  const compat = a.compatible_templates || [];
  const isDeprecated = !!a.deprecated;
  const hasNoIntent = !a.intent;

  // Global parameter registry — the SINGLE source of dropdown options for
  // EVERY automation. Sorted by category then label for readability.
  const paramOptions = useMemo(() => {
    const list = [...(availableParameters || [])];
    list.sort((x, y) => (x.category + x.label).localeCompare(y.category + y.label));
    return list;
  }, [availableParameters]);

  const [tpl,  setTpl]  = useState(initial.template);
  const [lang, setLang] = useState(initial.lang || "en");
  const [params, setParams] = useState((initial.params && initial.params.length) ? initial.params : (a.param_slots || []));
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testValues, setTestValues] = useState({});
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [open, setOpen] = useState(false);

  // Resolve the chosen template — prefer the compatible list (which has full
  // Meta metadata: parameter_format, named_slots, buttons) then fall back to
  // the global map (for the "not in approved list" edge case).
  const chosen = tpl
    ? (compat.find(t => t.name === tpl) || templatesByName.get(tpl))
    : null;
  const isNamed = chosen?.parameter_format === "NAMED"
                    || (chosen?.named_slots || []).length > 0;
  const namedSlots      = chosen?.named_slots      || [];
  const positionalSlots = chosen?.positional_slots || chosen?.slots || [];
  const buttons         = chosen?.buttons          || [];

  // Rebuild the parameter form when the template changes — pull the schema
  // ONLY from the newly-selected template's Meta components so we never
  // reuse a stale schema from a previous template.
  useEffect(() => {
    if (!chosen) return;
    if (isNamed) {
      setParams(namedSlots);
    } else if (positionalSlots.length > 0) {
      // For positional: prefill from mapping's stored order (if it matches
      // slot count) or from the automation's expected param names.
      const stored = initial.params || [];
      if (stored.length === positionalSlots.length) {
        setParams(stored);
      } else {
        setParams((a.param_slots || []).slice(0, positionalSlots.length));
      }
    } else {
      setParams([]);
    }
    // Also reset any lingering test values so we don't mix schemas.
    setTestValues({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tpl]);

  const save = async () => {
    if (!tpl) { toast.error("Pick a template"); return; }
    if (hasNoIntent) {
      toast.error("This automation has no canonical intent — cannot save.");
      return;
    }
    const expectedCount = isNamed ? namedSlots.length : positionalSlots.length;
    if (params.length !== expectedCount) {
      toast.error(`Template needs ${expectedCount} param${expectedCount === 1 ? "" : "s"} — got ${params.length}`);
      return;
    }
    setSaving(true);
    try {
      const payload = { template: tpl, lang, params };
      // Attach v2 fields when the template uses named parameters.
      if (isNamed) {
        payload.send_mode = "named";
        payload.named_params = namedSlots;
        // Standard button payload template (matches whatsapp_button_actions).
        if (buttons.length > 0) {
          const btnMap = { "Confirm": "appointment.confirm",
                             "Reschedule": "appointment.reschedule",
                             "Cancel": "appointment.cancel" };
          payload.button_payload_template = buttons.map(b => {
            const action = btnMap[b.text] || "appointment.unknown";
            return `${action}|{appointment_id}`;
          });
        }
      }
      await api.put(`/whatsapp/automations/${a.key}`, payload);
      toast.success("Mapping saved");
      onSaved?.();
    } catch (e) { toast.error(apiError(e, "Save failed")); }
    finally { setSaving(false); }
  };

  const clear = async () => {
    if (!window.confirm(`Remove mapping for ${a.label}? Automation will be blocked outside 24h window.`)) return;
    try {
      await api.delete(`/whatsapp/automations/${a.key}`);
      toast.success("Mapping cleared");
      setTpl(""); setParams(a.param_slots || []);
      onSaved?.();
    } catch (e) { toast.error(apiError(e, "Clear failed")); }
  };

  const doTest = async () => {
    if (!testTo) { toast.error("Enter recipient phone"); return; }
    setTesting(true); setTestResult(null);
    try {
      const r = await api.post(`/whatsapp/automations/${a.key}/test-send`,
                                 { to: testTo, param_values: testValues });
      setTestResult(r.data);
      if (r.data.ok) toast.success(`Sent · wamid ${r.data.message_id || r.data.wamid || ""}`);
      else toast.error(r.data.error || "Test failed");
    } catch (e) {
      setTestResult({ ok: false, error: apiError(e, "Test failed"), raw: e?.response?.data });
    } finally { setTesting(false); }
  };

  return (
    <div className="rounded-2xl border border-[#E7E1D3] bg-white p-4" data-testid={`auto-row-${a.key}`}>
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-xl bg-linen grid place-items-center text-ink-mid">
          <Zap className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-ink flex items-center gap-2">
            {a.label}
            {upgrades.length > 0 && (
              <button
                onClick={() => { setOpen(true); }}
                className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-violet-50 border border-violet-200 text-violet-800 hover:bg-violet-100"
                title={`Available upgrades: ${upgrades.map(u => u.template).join(", ")}`}
                data-testid={`auto-row-${a.key}-upgrade-chip`}>
                <ArrowUpCircle className="w-3 h-3" /> {upgrades.length} upgrade{upgrades.length === 1 ? "" : "s"}
              </button>
            )}
          </div>
          <div className="text-[11px] text-ink-mid truncate">{a.trigger}</div>
        </div>
        {a.mapping ? (
          <div className="inline-flex items-center gap-1.5 text-[11px] px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800">
            <CheckCircle2 className="w-3 h-3" /> {a.mapping.template}
          </div>
        ) : (
          <div className="inline-flex items-center gap-1.5 text-[11px] px-3 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-900">
            <AlertCircle className="w-3 h-3" /> Template required — not configured
          </div>
        )}
        <button onClick={() => setOpen(v => !v)} className="text-xs text-ink-mid hover:text-ink underline"
                data-testid={`auto-row-${a.key}-toggle`}>
          {open ? "Hide" : "Configure"}
        </button>
      </div>

      {open && (
        <div className="mt-4 pt-4 border-t border-[#E7E1D3] space-y-4">
          {/* Upgrade suggestion strip */}
          {upgrades.length > 0 && a.mapping && (
            <div className="rounded-xl border border-violet-200 bg-violet-50/40 p-3 flex items-start gap-3"
                 data-testid={`auto-row-${a.key}-upgrade-strip`}>
              <ArrowUpCircle className="w-4 h-4 text-violet-700 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-violet-900">
                  {upgrades.length === 1 ? "A newer template is available for this automation." : `${upgrades.length} newer templates match this automation.`}
                </div>
                <div className="text-[11px] text-ink-mid mt-1">
                  Current: <code className="font-mono">{a.mapping.template}</code>. Keep it, or switch by picking one below and clicking Save.
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <button
                    onClick={() => setTpl(a.mapping.template)}
                    className={`text-[10px] font-mono px-2 py-1 rounded-full border ${
                      tpl === a.mapping.template
                        ? "bg-emerald-600 text-white border-emerald-700"
                        : "bg-white text-ink border-stone-200 hover:bg-linen"
                    }`}
                    data-testid={`auto-row-${a.key}-keep-current`}>
                    Keep current
                  </button>
                  {upgrades.map(u => (
                    <button key={u.template}
                            onClick={() => setTpl(u.template)}
                            className={`text-[10px] font-mono px-2 py-1 rounded-full border inline-flex items-center gap-1 ${
                              tpl === u.template
                                ? "bg-violet-600 text-white border-violet-700"
                                : "bg-white text-violet-800 border-violet-200 hover:bg-violet-50"
                            }`}
                            data-testid={`auto-row-${a.key}-use-${u.template}`}>
                      Use {u.template} <ConfidencePill c={u.confidence} tiny />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Deprecated automation notice — save is blocked backend-side too */}
          {isDeprecated && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs text-rose-900"
                  data-testid={`auto-row-${a.key}-deprecated`}>
              This automation is deprecated. Use the timed variants
              <code className="font-mono mx-1">appointment_reminder_24h</code> and
              <code className="font-mono mx-1">appointment_reminder_2h</code> instead.
            </div>
          )}

          {/* Template pick + language + slot mapping */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="text-sm">
              <div className="text-[10px] uppercase tracking-widest text-ink-mid mb-1">
                Choose a template for this automation
                {a.intent && (
                  <code className="ml-2 text-[10px] font-mono text-ink-mid">
                    · intent: {a.intent}
                  </code>
                )}
              </div>
              {compat.length === 0 && !tpl ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                      data-testid={`auto-${a.key}-no-compatible`}>
                  {templates && templates.length > 0 ? (
                    <>
                      No template mapped to <code className="font-mono">{a.intent || a.key}</code>. Approved templates exist in your WABA but none are recognised for this intent — check the intent registry or add one.
                    </>
                  ) : (
                    <>
                      No approved / active Meta template is available for this automation yet.{" "}
                      <span className="opacity-80">Templates in <em>Pending</em>, <em>Rejected</em> or <em>Paused</em> status can't be sent. Use the diagnostic at <code className="font-mono">/api/whatsapp/templates/diagnostic?template_name=…</code> to check a specific template.</span>
                    </>
                  )}
                </div>
              ) : (
                <select value={tpl} onChange={e => setTpl(e.target.value)}
                         disabled={isDeprecated}
                         data-testid={`auto-${a.key}-template`}
                         className="w-full rounded-lg border border-[#E7E1D3] px-3 py-2 text-sm bg-white disabled:opacity-50">
                  <option value="">— pick a template —</option>
                  {/* Show the current mapping if it's no longer approved. */}
                  {tpl && !compat.find(t => t.name === tpl) && (
                    <option value={tpl}>{tpl} · {lang} (not in approved list)</option>
                  )}
                  {compat.map(t => (
                    <option key={t.name} value={t.name}>
                      {t.name} · {t.language} · {(t.parameter_format || "POSITIONAL").toLowerCase()}
                      {(t.buttons || []).length > 0 ? " · buttons" : ""}
                    </option>
                  ))}
                </select>
              )}
              {tpl && !compat.find(t => t.name === tpl) && (
                <div className="mt-1 text-[10px] text-amber-800">
                  This template isn't in the currently approved list — Meta may reject sends.
                </div>
              )}
            </label>
            <label className="text-sm">
              <div className="text-[10px] uppercase tracking-widest text-ink-mid mb-1">Language</div>
              <input value={lang} onChange={e => setLang(e.target.value)}
                     data-testid={`auto-${a.key}-lang`}
                     className="w-full rounded-lg border border-[#E7E1D3] px-3 py-2 text-sm font-mono" />
            </label>
            <div className="flex items-end gap-2">
              <button onClick={save} disabled={saving || !tpl || isDeprecated}
                      className="inline-flex items-center gap-2 text-sm font-medium text-white bg-ink hover:bg-black px-4 py-2 rounded-full disabled:opacity-40"
                      data-testid={`auto-${a.key}-save`}>
                <Save className="w-3.5 h-3.5" /> Save
              </button>
              {a.mapping && (
                <button onClick={clear} className="text-sm text-rose-700 hover:bg-rose-50 rounded-full px-3 py-2"
                        data-testid={`auto-${a.key}-clear`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {chosen && (
            <div className="rounded-xl bg-linen border border-[#E7E1D3] p-3 space-y-2">
              <div className="text-[11px] uppercase tracking-widest text-ink-mid flex items-center gap-2 flex-wrap">
                <span>Template body preview · {chosen.category}</span>
                <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded-full border ${
                  isNamed
                    ? "bg-violet-50 border-violet-200 text-violet-800"
                    : "bg-stone-100 border-stone-200 text-ink-mid"
                }`}>
                  {isNamed ? "NAMED params" : "POSITIONAL params"}
                </span>
                {buttons.length > 0 && (
                  <span className="text-[9px] font-mono px-1.5 py-0.5 rounded-full border bg-indigo-50 border-indigo-200 text-indigo-800">
                    {buttons.length} quick-reply button{buttons.length === 1 ? "" : "s"}
                  </span>
                )}
              </div>
              <div className="text-xs text-ink whitespace-pre-wrap font-mono">{chosen.body}</div>
              {buttons.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {buttons.map((b, i) => (
                    <span key={i} className="text-[10px] px-2 py-1 rounded-full border border-indigo-200 bg-white text-indigo-800 font-mono">
                      [{b.type}] {b.text}
                    </span>
                  ))}
                </div>
              )}
              {/* Dynamic parameter mapping — named vs positional. */}
              {isNamed && namedSlots.length > 0 && (
                <div className="space-y-1.5">
                  <div className="text-[10px] uppercase tracking-widest text-ink-mid">
                    Meta named parameters (sent as parameter_name)
                  </div>
                  {namedSlots.map(name => (
                    <div key={name} className="flex items-center gap-2 text-sm">
                      <div className="font-mono text-xs text-ink-mid w-32 truncate">{"{{" + name + "}}"}</div>
                      <div className="flex-1 text-[11px] text-ink-mid">
                        Auto-filled at send time from Viyora's automation context
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {!isNamed && positionalSlots.length > 0 && (
                <div className="space-y-1.5">
                  <div className="text-[10px] uppercase tracking-widest text-ink-mid">
                    Map each &#123;&#123;N&#125;&#125; to a Viyora automation param
                  </div>
                  {positionalSlots.map(slot => (
                    <div key={slot} className="flex items-center gap-2 text-sm">
                      <div className="font-mono text-xs text-ink-mid w-14">{"{{" + slot + "}}"}</div>
                      <select value={params[slot - 1] || ""}
                              onChange={e => {
                                const next = [...params];
                                next[slot - 1] = e.target.value;
                                setParams(next);
                              }}
                              data-testid={`auto-${a.key}-param-${slot}`}
                              className="flex-1 rounded-lg border border-[#E7E1D3] px-3 py-1.5 text-sm bg-white">
                        <option value="">— pick a param —</option>
                        {paramOptions.length > 0 ? (
                          Object.entries(
                            paramOptions.reduce((acc, p) => {
                              (acc[p.category] = acc[p.category] || []).push(p);
                              return acc;
                            }, {})
                          ).map(([cat, items]) => (
                            <optgroup key={cat} label={cat}>
                              {items.map(p => (
                                <option key={p.key} value={p.key} title={p.description}>
                                  {p.label} · {p.key}
                                </option>
                              ))}
                            </optgroup>
                          ))
                        ) : (
                          (a.param_slots || []).map(p => <option key={p} value={p}>{p}</option>)
                        )}
                      </select>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Test send — dynamic fields from the SELECTED template's actual Meta components */}
          {chosen && (
            <div className="rounded-xl border border-[#E7E1D3] bg-white p-3 space-y-2" data-testid={`auto-${a.key}-test`}>
              <div className="text-[10px] uppercase tracking-widest text-ink-mid">
                Test send · fields generated from template metadata
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                <input value={testTo} onChange={e => setTestTo(e.target.value)}
                       placeholder="Recipient (e.g. 919810453892)"
                       className="rounded-lg border border-[#E7E1D3] px-3 py-2 text-sm font-mono"
                       data-testid={`auto-${a.key}-test-to`} />
                {(isNamed ? namedSlots : positionalSlots.map(n => `param_${n}`)).map(name => (
                  <input key={name} value={testValues[name] || ""}
                          onChange={e => setTestValues({ ...testValues, [name]: e.target.value })}
                          placeholder={name}
                          data-testid={`auto-${a.key}-test-field-${name}`}
                          className="rounded-lg border border-[#E7E1D3] px-3 py-2 text-sm" />
                ))}
                <button onClick={doTest} disabled={testing}
                        className="col-span-full md:col-auto inline-flex items-center justify-center gap-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 px-4 py-2 rounded-full"
                        data-testid={`auto-${a.key}-test-btn`}>
                  {testing ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                  Send test
                </button>
              </div>
              {testResult && (
                <pre className="text-[10px] bg-stone-50 rounded p-2 overflow-x-auto max-h-40 whitespace-pre-wrap"
                      data-testid={`auto-${a.key}-test-result`}>
                  {JSON.stringify(testResult, null, 2)}
                </pre>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
